/**
 * Neron API client — CRA frontend talks to Express at /api/*
 * Set REACT_APP_API_URL (e.g. https://ipbx.bmtaxopc.com or http://localhost:5000).
 * Empty = same origin (production behind Traefik).
 */

function resolveApiUrl() {
  if (process.env.REACT_APP_API_URL != null && String(process.env.REACT_APP_API_URL).trim() !== "") {
    return String(process.env.REACT_APP_API_URL).trim().replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    const { port, origin } = window.location;
    // Prefer 127.0.0.1 — Windows browsers often resolve localhost to ::1,
    // while the API listens on IPv4 only.
    if (port === "3000" || port === "3001") {
      return "http://127.0.0.1:5000";
    }
    return origin;
  }
  return "http://127.0.0.1:5000";
}

export const API_URL = resolveApiUrl();

/** Optional local/contacts host (name book + caller-lookup enrichment). */
function resolveContactsApiUrl() {
  const raw = process.env.REACT_APP_CONTACTS_API_URL;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim().replace(/\/$/, "");
  }
  return API_URL;
}

export const CONTACTS_API_URL = resolveContactsApiUrl();

const TOKEN_KEY = "neron_token";
const APP_ID_KEY = "neron_app_id";

export function getToken() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token: string) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getActiveAppId() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(APP_ID_KEY) || "";
}

export function setActiveAppId(id: string | number | null | undefined) {
  if (typeof window === "undefined") return;
  if (id == null || id === "") localStorage.removeItem(APP_ID_KEY);
  else localStorage.setItem(APP_ID_KEY, String(id));
}

/** Kept for dilex-compatible imports; neron uses a single JWT. */
export function getRefreshToken() {
  return "";
}

export function setRefreshToken(_token: string) {
  /* no-op */
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(APP_ID_KEY);
}

function withApiPrefix(path: string, base = API_URL) {
  if (path.startsWith("http")) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  if (p.startsWith("/api/")) return `${base}${p}`;
  return `${base}/api${p}`;
}

/**
 * Fetch helper. Paths are relative to /api (e.g. "/auth/me", "/pbx/calls").
 * Unwraps neron `{ status, data, token, message }` envelopes when present.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiAt<T>(API_URL, path, init, { logoutOnUnauthorized: true });
}

/** Contacts / caller-lookup against CONTACTS_API_URL (may differ from PBX API). */
export async function contactsApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Never treat a secondary contacts host 401 as a global logout — JWT/secret
  // mismatch or a down local API must not kick the agent out mid-call.
  return apiAt<T>(CONTACTS_API_URL, path, init, { logoutOnUnauthorized: false });
}

async function apiAt<T>(
  base: string,
  path: string,
  init: RequestInit = {},
  opts: { logoutOnUnauthorized?: boolean } = {},
): Promise<T> {
  const logoutOnUnauthorized = opts.logoutOnUnauthorized !== false;
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const appId = getActiveAppId();
  if (appId) headers.set("X-App-Id", appId);

  let res: Response;
  try {
    res = await fetch(withApiPrefix(path, base), { ...init, headers });
  } catch {
    throw new Error(
      `Cannot reach API at ${base}. Start neron server (cd server && npm run dev). On Windows use http://127.0.0.1:5000, not localhost.`,
    );
  }

  if (res.status === 401) {
    if (logoutOnUnauthorized && !path.includes("/auth/")) {
      clearToken();
      if (typeof window !== "undefined") window.location.href = "/";
    }
    const err = new Error("Unauthorized") as Error & { status?: number };
    err.status = 401;
    throw err;
  }

  return readJson<T>(res, path, { logoutOnUnauthorized });
}

async function readJson<T>(
  res: Response,
  path: string,
  opts: { logoutOnUnauthorized?: boolean } = {},
): Promise<T> {
  const logoutOnUnauthorized = opts.logoutOnUnauthorized !== false;
  const raw = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    if (res.status === 401 && logoutOnUnauthorized && !path.includes("/auth/")) {
      clearToken();
      if (typeof window !== "undefined") window.location.href = "/";
    }
    const env = raw as {
      message?: string;
      error?: string;
      hint?: string;
      code?: string;
      conflicts?: Array<Record<string, unknown>>;
    };
    let msg = env.message || env.error || `Request failed (${res.status})`;
    if (env.hint) msg = `${msg} — ${env.hint}`;
    if (Array.isArray(env.conflicts) && env.conflicts.length) {
      const extra = env.conflicts
        .map((c) => {
          const label = String(c.field_label || c.field || "field");
          const app = String(c.used_by_app_name || c.used_by_app_id || "another app");
          const val = String(c.value || "");
          return `${label} "${val}" → used by ${app}`;
        })
        .join("; ");
      msg = `${msg} Conflicts: ${extra}`;
    }
    const err = new Error(String(msg)) as Error & {
      code?: string;
      conflicts?: unknown;
      hint?: string;
      status?: number;
    };
    err.code = env.code;
    err.conflicts = env.conflicts;
    err.hint = env.hint;
    err.status = res.status;
    throw err;
  }

  // Neron envelope: { status, data, token, message }
  if (raw && typeof raw === "object" && "status" in raw && ("data" in raw || "token" in raw)) {
    const env = raw as {
      status: string;
      data?: unknown;
      token?: string;
      message?: string;
      [k: string]: unknown;
    };
    if (env.token && env.data !== undefined) {
      return { token: env.token, data: env.data, message: env.message, ...env } as T;
    }
    if (env.data !== undefined) {
      // Prefer returning data directly when callers expect the entity
      return env.data as T;
    }
  }

  return raw as T;
}
